/**
 * Прогрев: то, что оверлей скачивает заранее.
 *
 * ЧТО ПРОГРЕВАЕТСЯ И ПОЧЕМУ ИМЕННО ЭТО (docs/04):
 *
 *   звуки саундборда → AudioBuffer. `<audio>` даёт 100–300 мс
 *     до первого звука: нажатая в эфире кнопка опаздывает
 *     к моменту, ради которого её нажали;
 *   шрифты → FontFace. Файл весит сотни килобайт, и без прогрева
 *     надпись выйдет запасным шрифтом и перерисуется своим;
 *   картинки → скрытый пул с decode(). Этим занимается сам виджет
 *     через общий кеш: хост зовёт `preload` для припаркованного;
 *   видео → только метаданные. Скачать ролик целиком ради слоя,
 *     который, может быть, никогда не понадобится, значит потратить
 *     полосу стримера во время эфира.
 *
 * ЭМБЕДЫ НЕ ПРОГРЕВАЮТСЯ ВОВСЕ: их содержимое нам недоступно.
 *
 * БЮДЖЕТ ОБЩИЙ И КОНЕЧНЫЙ — 150 МБ. Оверлей работает часами рядом
 * с игрой и кодировщиком, и прогрев без предела съел бы память,
 * которой там и так впритык. Что не влезло — вытесняется по LRU.
 */

import type { WarmSound } from '@owm/scene';
import { bufferBytes, setWarmCache, WarmCache, type WarmValue } from '@owm/widgets';

/** Как скачать файл. Подменяется в тестах: сеть в тесте — гадание. */
export type Fetcher = (url: string) => Promise<ArrayBuffer>;

/** Как превратить скачанное в буфер. */
export type Decoder = (data: ArrayBuffer) => Promise<AudioBuffer>;

export interface WarmupOptions {
  readonly fetch?: Fetcher;
  readonly decode?: Decoder;
  readonly budgetBytes?: number;
}

/** Что получилось прогреть. */
export interface WarmReport {
  /** Сколько звуков легло в кеш. */
  readonly warmed: number;
  /** Сколько не удалось: не скачались, не разобрались, не влезли. */
  readonly failed: number;
}

/**
 * Прогрев оверлея.
 *
 * Классом, потому что кеш надо где-то держать, а объявить его
 * виджетам — отдельным действием: они берут его из модульной
 * переменной `@owm/widgets`, как и звуковой граф.
 */
export class Warmup {
  readonly cache: WarmCache<WarmValue>;

  private readonly fetchBytes: Fetcher | null;
  private readonly decode: Decoder | null;

  /** Что уже прогрето или прогревается: повторно не качаем. */
  private readonly seen = new Set<string>();

  constructor(context: AudioContext | null, options: WarmupOptions = {}) {
    this.cache = new WarmCache<WarmValue>({
      ...(options.budgetBytes === undefined ? {} : { budgetBytes: options.budgetBytes }),
      onEvict: (key, value) => {
        // Вытесненная картинка обязана отпустить память декодера:
        // без сброса адреса растр висит в браузере и после того,
        // как ссылок на элемент не осталось
        if (value.kind === 'image') value.el.removeAttribute('src');
        this.seen.delete(key);
      },
    });

    this.fetchBytes = options.fetch ?? defaultFetch();
    this.decode = options.decode ?? decoderFor(context);

    // Кеш объявляется виджетам сразу: картинку припаркованного слоя
    // прогревает сам виджет, и без объявленного кеша она ушла бы
    // мимо бюджета
    setWarmCache(this.cache);
  }

  /**
   * Прогревает звуки в том порядке, в котором их прислал сервер.
   *
   * Порядок не случаен: сервер шлёт список в порядке сетки, а под
   * пальцем у стримера лежит то, что он жмёт чаще. Бюджет конечен,
   * и прогревать первым стоит именно это.
   *
   * ПОСЛЕДОВАТЕЛЬНО, А НЕ ВСЁ СРАЗУ: десяток параллельных загрузок
   * на старте отнимает полосу у самого эфира, а торопиться некуда —
   * до первого нажатия обычно есть минуты.
   */
  async warmSounds(sounds: readonly WarmSound[]): Promise<WarmReport> {
    let warmed = 0;
    let failed = 0;

    for (const sound of sounds) {
      const ok = await this.warmSound(sound.url);
      if (ok) warmed += 1;
      else failed += 1;
    }

    return { warmed, failed };
  }

  /** Прогревает один звук. Возвращает, получилось ли. */
  async warmSound(url: string): Promise<boolean> {
    if (url === '' || this.fetchBytes === null || this.decode === null) return false;
    if (this.seen.has(url)) return false;

    this.seen.add(url);

    try {
      const data = await this.fetchBytes(url);
      const buffer = await this.decode(data);

      const put = this.cache.put(url, { kind: 'sound', buffer }, bufferBytes(buffer));
      if (!put) {
        // Не влезает в бюджет ЦЕЛИКОМ — значит и не влезет никогда.
        // Забываем, чтобы не пытаться снова при следующем прогреве
        this.seen.delete(url);
      }

      return put;
    } catch {
      // Не скачался, не разобрался, отвалилась сеть. Молча: прогрев
      // ускоряет, но не решает, играть ли звук, — при нажатии
      // сработает запасной путь через <audio>
      this.seen.delete(url);

      return false;
    }
  }

  /** Отпускает всё. Нужно при закрытии страницы. */
  stop(): void {
    this.cache.clear();
    this.seen.clear();
    setWarmCache(null);
  }
}

/**
 * Загрузчик по умолчанию.
 *
 * `cors`, потому что раздача ассетов — чужой origin для страницы
 * оверлея. Без этого ответ был бы непрозрачным, и `arrayBuffer()`
 * вернул бы пустоту вместо звука.
 */
function defaultFetch(): Fetcher | null {
  const load = globalThis.fetch;
  if (typeof load !== 'function') return null;

  return async (url: string): Promise<ArrayBuffer> => {
    const response = await load(url, { mode: 'cors', credentials: 'omit' });
    if (!response.ok) {
      throw new Error(`прогрев ${url}: ответ ${response.status}`);
    }

    return response.arrayBuffer();
  };
}

/**
 * Декодер поверх AudioContext.
 *
 * Тем же контекстом, что и играет: буфер, декодированный чужим
 * контекстом, в этом не зазвучит — частоты дискретизации у них
 * разные.
 */
function decoderFor(context: AudioContext | null): Decoder | null {
  if (context === null) return null;

  return async (data: ArrayBuffer): Promise<AudioBuffer> => context.decodeAudioData(data);
}
